import { useState } from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Calendar } from './ui/calendar'
import { cn } from '../lib/utils'
import { formatDate } from '../lib/translations'

// 'YYYY-MM-DD' <-> local Date (no timezone drift; constructed at local midnight).
export const strToDate = (s?: string): Date | undefined => {
  if (!s) return undefined
  const [y, m, d] = s.split('-').map(Number)
  if (!y || !m || !d) return undefined
  return new Date(y, m - 1, d)
}

export const dateToStr = (d?: Date | null): string => {
  if (!d) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

type DatePickerProps = {
  value?: string
  onChange: (value: string) => void
  min?: string
  max?: string
  placeholder?: string
  language?: string
  disabled?: boolean
  className?: string
  fromYear?: number
  toYear?: number
  'data-testid'?: string
}

const DatePicker = ({
  value,
  onChange,
  min,
  max,
  placeholder,
  language = 'en',
  disabled = false,
  className = '',
  fromYear,
  toYear,
  'data-testid': testId,
}: DatePickerProps) => {
  const [open, setOpen] = useState(false)
  const selected = strToDate(value)
  const minDate = strToDate(min)
  const maxDate = strToDate(max)
  const disabledMatchers: any[] = []
  if (minDate) disabledMatchers.push({ before: minDate })
  if (maxDate) disabledMatchers.push({ after: maxDate })

  // Range backing the month/year dropdowns. Derive from min/max when present so
  // the year selector only offers valid years; otherwise use a generous window.
  const currentYear = new Date().getFullYear()
  const startYear = fromYear ?? (minDate ? minDate.getFullYear() : currentYear - 100)
  const endYear = toYear ?? (maxDate ? maxDate.getFullYear() : currentYear + 10)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'flex h-12 w-full items-center gap-3 rounded-2xl border border-[#1b1033]/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 text-left text-[15px] transition-colors hover:border-[#ff4d8d]/40 dark:hover:border-white/20 focus-visible:outline-none focus-visible:border-[#ff4d8d] focus-visible:ring-2 focus-visible:ring-[#ff4d8d]/25 disabled:opacity-50 motion-reduce:transition-none',
            className,
          )}
        >
          <CalendarIcon className="w-5 h-5 text-[#8b5cf6]/70 shrink-0" />
          <span className={selected ? 'text-[#1b1033] dark:text-[#f4f0ff]' : 'text-[#4a3d66]/50 dark:text-[#c3b8dd]/60'}>
            {selected
              ? formatDate(value, language)
              : placeholder || (language === 'ka' ? 'აირჩიეთ თარიღი' : 'Pick a date')}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown-buttons"
          fromYear={startYear}
          toYear={endYear}
          selected={selected}
          defaultMonth={selected}
          onSelect={(d: Date | undefined) => {
            onChange(dateToStr(d))
            if (d) setOpen(false)
          }}
          disabled={disabledMatchers.length ? disabledMatchers : undefined}
          initialFocus
        />
        <div className="flex justify-end border-t border-[#1b1033]/10 dark:border-white/10 p-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full px-3 py-1.5 text-sm font-semibold text-[#ff4d8d] transition-colors hover:bg-[#ff4d8d]/10 motion-reduce:transition-none"
            data-testid={testId ? `${testId}-done` : undefined}
          >
            {language === 'ka' ? 'მზადაა' : 'Done'}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DatePicker
